import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Keeps the embedded n8n editor alive across navigation.
 *
 * The editor used to be an iframe inside the Workflows page, so any sidebar
 * link unmounted it: unsaved edits were lost and coming back reloaded n8n from
 * scratch. Now one iframe lives here, above the routes. The page only marks
 * where it should appear (a {@link WorkflowEditorSlot}); while that slot is
 * mounted the iframe is pinned over it, and when it unmounts the iframe is
 * hidden but kept loaded.
 *
 * It is dropped when the signed-in user changes (so the next person never sees
 * the last session's editor) and after a long spell hidden, to give back the
 * memory n8n's editor holds.
 */

/** Hidden this long, the editor is unloaded. */
const IDLE_UNLOAD_MS = 30 * 60 * 1000;

interface Rect { top: number; left: number; width: number; height: number }

interface EditorContextValue {
  attach: (el: HTMLElement, src: string, title: string) => void;
  detach: (el: HTMLElement) => void;
  reload: () => void;
  loaded: boolean;
}

const EditorContext = createContext<EditorContextValue | null>(null);

export const WorkflowEditorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [src, setSrc] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);
  const [loaded, setLoaded] = useState(false);
  // Bumped by reload() to remount the iframe back at its start page.
  const [frameKey, setFrameKey] = useState(0);

  // Mirrors `src` so attach() can tell a new address from a re-attach.
  const srcRef = useRef<string | null>(null);

  const drop = useCallback(() => {
    srcRef.current = null;
    setSrc(null);
    setLoaded(false);
  }, []);

  // A different user (or signing out) never inherits the editor.
  const userId = user?.userId;
  useEffect(() => drop, [userId, drop]);

  const attach = useCallback((el: HTMLElement, nextSrc: string, nextTitle: string) => {
    setSlot(el);
    setTitle(nextTitle);
    if (srcRef.current !== nextSrc) {
      srcRef.current = nextSrc;
      setSrc(nextSrc);
      setLoaded(false);
    }
  }, []);

  const detach = useCallback((el: HTMLElement) => {
    setSlot((current) => (current === el ? null : current));
  }, []);

  const reload = useCallback(() => {
    setLoaded(false);
    setFrameKey((k) => k + 1);
  }, []);

  // Pin the iframe over the slot, following it through resizes and the
  // sidebar collapsing/expanding (which changes the slot's size).
  useLayoutEffect(() => {
    if (!slot) {
      setRect(null);
      return;
    }
    const place = () => {
      const r = slot.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    place();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null;
    observer?.observe(slot);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [slot]);

  // Unload after a long time out of sight.
  useEffect(() => {
    if (slot || !src) return;
    const id = window.setTimeout(drop, IDLE_UNLOAD_MS);
    return () => window.clearTimeout(id);
  }, [slot, src, drop]);

  const visible = Boolean(slot && rect && loaded);

  return (
    <EditorContext.Provider value={{ attach, detach, reload, loaded }}>
      {children}
      {src && (
        <iframe
          key={`${frameKey}:${src}`}
          src={src}
          title={title}
          className="workflows-frame workflows-frame--hosted"
          allow="clipboard-read; clipboard-write"
          referrerPolicy="strict-origin-when-cross-origin"
          onLoad={() => setLoaded(true)}
          // Kept in the page while hidden, but out of sight, reach and the tab order.
          aria-hidden={visible ? undefined : true}
          tabIndex={visible ? undefined : -1}
          style={{
            position: 'fixed',
            top: rect?.top ?? 0,
            left: rect?.left ?? 0,
            width: rect?.width ?? 0,
            height: rect?.height ?? 0,
            visibility: visible ? 'visible' : 'hidden',
            pointerEvents: visible ? 'auto' : 'none',
          }}
        />
      )}
    </EditorContext.Provider>
  );
};

interface SlotProps {
  src: string;
  title: string;
  loadingLabel: string;
}

/**
 * Where the hosted editor appears. Without a provider (tests, or a page used
 * on its own) it falls back to a plain iframe of its own.
 */
export const WorkflowEditorSlot: React.FC<SlotProps> = ({ src, title, loadingLabel }) => {
  const host = useContext(EditorContext);
  const ref = useRef<HTMLDivElement>(null);
  const [ownLoaded, setOwnLoaded] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!host || !el) return;
    host.attach(el, src, title);
    return () => host.detach(el);
  }, [host, src, title]);

  const loaded = host ? host.loaded : ownLoaded;

  return (
    <div className="workflows-frame-wrap" ref={ref}>
      {!loaded && (
        <div className="workflows-state workflows-frame-loading" role="status">
          <Loader2 className="spinning" size={22} aria-hidden="true" />
          <span>{loadingLabel}</span>
        </div>
      )}
      {!host && (
        <iframe
          src={src}
          title={title}
          className="workflows-frame"
          allow="clipboard-read; clipboard-write"
          referrerPolicy="strict-origin-when-cross-origin"
          onLoad={() => setOwnLoaded(true)}
        />
      )}
    </div>
  );
};

/** "Reload" for whichever editor the slot shows; null without a provider. */
export const useWorkflowEditorReload = (): (() => void) | null => useContext(EditorContext)?.reload ?? null;
